import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AgentAccessService } from "@/services/agent/AgentAccessService";
import { EdgeFunctionLogsService } from "@/services/EdgeFunctionLogsService";
import type { EdgeFunctionLogFilters } from "@/types/edge-function-logs";

export const useEdgeFunctionLogs = (
  filters?: EdgeFunctionLogFilters,
  page: number = 1,
  pageSize: number = 50,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: ["edge-function-logs", filters, page, pageSize],
    queryFn: () => EdgeFunctionLogsService.getEdgeFunctionLogs(filters, pageSize, (page - 1) * pageSize),
    staleTime: 1000 * 60,
    refetchInterval: enabled ? 1000 * 60 : false,
    enabled,
  });
};

export const useEdgeFunctionLog = (id: string | null) => {
  return useQuery({
    queryKey: ["edge-function-log", id],
    queryFn: () => EdgeFunctionLogsService.getEdgeFunctionLog(id as string),
    enabled: !!id,
  });
};

export const useAgentAccessDiagnosis = (agentId: string | null, userId: string | null) => {
  return useQuery({
    queryKey: ["agent-access-diagnosis", agentId, userId],
    queryFn: () => AgentAccessService.diagnose(agentId as string, userId as string),
    enabled: !!agentId && !!userId,
  });
};

export const useResolveEdgeFunctionLog = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => EdgeFunctionLogsService.resolveLog(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["edge-function-logs"] });
      queryClient.invalidateQueries({ queryKey: ["edge-function-log-stats"] });
    },
  });
};

export const useEdgeFunctionLogStats = (filters?: EdgeFunctionLogFilters, enabled: boolean = true) => {
  return useQuery({
    queryKey: ["edge-function-log-stats", filters],
    queryFn: () => EdgeFunctionLogsService.getEdgeFunctionLogStats(filters),
    staleTime: 1000 * 60,
    refetchInterval: enabled ? 1000 * 60 : false,
    enabled,
  });
};
